import { usePagination, useRequest } from 'ahooks';
import { message } from 'antd';
import { useSetReturnCancelCount } from 'hooks/business/openReturnStatus';
import { useCallback, useMemo, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import {
  ProcessStatus,
  queryCancelOrderList,
  getTalkInfoByBuyerStore,
  queryCancelState,
} from 'services/api/refund';
import toLowerCase from 'utils/toLowerCase';
import type { FilterState } from './useFilter';
import useGetStoreIdList from './useGetStoreIdList';

const useCancelList = (query: FilterState, platform: General.PlatformUpperCase) => {
  const [statusFilter, setStatusFilter] = useState<ProcessStatus | 'ALL'>('ALL');
  const { t } = useTranslate();
  const refreshCount = useSetReturnCancelCount();
  const getStoreIdList = useGetStoreIdList();
  const statusParams = useMemo(() => {
    if (statusFilter === 'PENDING') {
      return {
        orderState: 'LE_CHAT_IN_CANCEL',
      } as const;
    }
    if (statusFilter === 'PROCESS') {
      return {
        orderState: 'LE_CHAT_IN_CANCEL',
        cancelBeingProcessed: true,
      } as const;
    }
    if (statusFilter === 'COMPLETE') {
      return {
        orderState: 'LE_CHAT_CANCELED',
      } as const;
    }
    return {} as const;
  }, [statusFilter]);

  const {
    data,
    pagination,
    loading,
    refresh: refreshList,
  } = usePagination(
    async ({ pageSize, current }) => {
      try {
        const { startDate, endDate, orderSn: orderId, region, follower, storeList } = query;
        const internalStoreIdList = await getStoreIdList({ region, follower, storeList }, platform);
        const fetch = (current: number): ReturnType<typeof queryCancelOrderList> =>
          queryCancelOrderList({
            orderId,
            createTimeBegin: startDate,
            createTimeEnd: endDate,
            pageNo: `${current}`,
            pageSize: `${pageSize}`,
            platform,
            ...statusParams,
            region,
            internalStoreIdList,
          }).then(({ data, total }) => {
            if (data.length === 0 && total === 0 && current > 1) {
              return fetch(current - 1);
            }
            return { data, total };
          });
        const { data: list, total } = await fetch(current);
        refreshCount();
        return {
          list,
          total,
        };
      } catch (error: any) {
        message.error(t('ViewRefund.获取取消订单列表失败'));
      }
      return {
        list: [],
        total: 0,
      };
    },
    {
      refreshDeps: [query, statusFilter, platform],
    },
  );
  const { data: conversationList } = useRequest(
    async () => {
      if (!data || !data.list) {
        return [];
      }
      try {
        const items = data.list.map((x) => {
          const { region, buyerId: externalBuyerId, externalStoreId } = x;
          return {
            channel: platform,
            region,
            externalBuyerId,
            externalStoreId,
          };
        });
        const { talkInfos } = await getTalkInfoByBuyerStore({ items });
        return talkInfos;
      } catch (error) {
        return [];
      }
    },
    {
      refreshDeps: [data],
    },
  );
  const { data: statusCount, refresh: refreshTabCount } = useRequest(
    async () => {
      const { startDate = '', endDate = '', orderSn, region, follower, storeList } = query;

      const internalStoreIdList = await getStoreIdList({ region, follower, storeList }, platform);

      return queryCancelState({
        // ...restQuery,
        // processStatus: statusFilter === 'ALL' ? undefined : statusFilter,
        createTimeBegin: parseInt(startDate, 10),
        createTimeEnd: parseInt(endDate, 10),
        platform: toLowerCase(platform),
        orderId: orderSn,
        region,
        internalStoreIdList,
      });
    },
    {
      refreshDeps: [query, statusFilter, platform],
    },
  );
  const refresh = useCallback(() => {
    refreshList();
    refreshTabCount();
  }, [refreshList, refreshTabCount]);
  return {
    list: data?.list,
    setStatusFilter,
    statusFilter,
    pagination,
    loading,
    statusCount,
    refresh,
    conversationList,
  };
};

export default useCancelList;
